import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../hooks/useLanguage";
import { useUpdateLanguageMutation } from "../api";

const LanguageSelector: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { currentLanguage, changeLanguage } = useLanguage();
    const [updateLanguage] = useUpdateLanguageMutation();

    const languages = [
        { code: "ru", name: "Русский", flag: "🇷🇺" },
        { code: "en", name: "English", flag: "🇺🇸" },
        { code: "ka", name: "ქართული", flag: "🇬🇪" },
        { code: "uk", name: "Українська", flag: "🇺🇦" },
    ];

    const currentLanguageData =
        languages.find((lang) => lang.code === currentLanguage) || languages[0];

    // Закрываем dropdown при клике вне его
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleLanguageChange = async (languageCode: string) => {
        try {
            await updateLanguage(languageCode).unwrap();
            changeLanguage(languageCode);
            setIsOpen(false);
        } catch (error) {
            console.error("Failed to update language:", error);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors w-full"
            >
                <span className="text-lg">{currentLanguageData.flag}</span>
                <span className="flex-1 text-left">
                    {currentLanguageData.name}
                </span>
                <svg
                    className={`w-4 h-4 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    {languages.map((language) => (
                        <button
                            key={language.code}
                            onClick={() => handleLanguageChange(language.code)}
                            className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                currentLanguage === language.code
                                    ? "bg-blue-50 dark:bg-blue-900/20"
                                    : ""
                            }`}
                        >
                            <span className="text-lg mr-3">
                                {language.flag}
                            </span>
                            {language.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSelector;
